import React, { useEffect, useState } from "react";
import authApi from "../../../api/authApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { Table, Button, Input, message, Card, Statistic, Spin, DatePicker } from "antd";
import { format, getUnixTime, startOfDay, endOfDay } from "date-fns";
import dayjs, { type Dayjs } from "dayjs";
import { DATE_PRESETS } from "../../../utils/date-presets";
import ReferrerEditModal, { type ReferrerEditTarget } from "../../../components/ReferrerEditModal";
import AssignReferrerSearch from "./assign-referrer-search";

const { RangePicker } = DatePicker;

interface ReferredUser {
    userId: number;
    name: string;
    email: string;
    createTime: number;
    refId: number | null;
    refName: string | null;
    refEmail: string | null;
    totalCommission: number;
    approvedCommission: number;
    pendingCommission: number;
    completedOrders: number;
    totalOrders: number;
}

function ReferredUsers() {
    const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [statistics, setStatistics] = useState({
        totalReferredUsers: 0,
        totalCommission: 0,
        totalApprovedCommission: 0,
        totalPendingCommission: 0,
    });
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    // Sửa cặp giới thiệu đã có. User chưa có ref không xuất hiện ở trang này — gán mới làm ở /user.
    const [refTarget, setRefTarget] = useState<ReferrerEditTarget | null>(null);
    const [filter, setFilter] = useState({
        limit: 20,
        page: 1,
        refId: "",
        refEmail: "",
        startTime: "",
        endTime: "",
        sort: "-createTime",
    });

    useEffect(() => {
        fetchStatistics();
    }, []);

    useEffect(() => {
        fetchReferredUsers();
    }, [filter]);

    const fetchStatistics = async () => {
        setStatisticsLoading(true);
        try {
            const { data } = await authApi.getRefStatistics();
            setStatistics({
                totalReferredUsers: data.statistics.totalReferredUsers || 0,
                totalCommission: data.statistics.totalRefCommission || 0,
                totalApprovedCommission: data.statistics.totalApprovedCommission || 0,
                totalPendingCommission: data.statistics.totalPendingCommission || 0,
            });
        } catch (error) {
            console.error("Failed to fetch statistics:", error);
            message.error("Không thể tải thống kê");
        } finally {
            setStatisticsLoading(false);
        }
    };

    const fetchReferredUsers = async () => {
        setIsLoading(true);
        const queryString = Object.keys(filter)
            .reduce((acc: string[], cur) => {
                const value = filter[cur as keyof typeof filter];
                if (value) {
                    acc.push(`${cur}=${value}`);
                }
                return acc;
            }, [])
            .join("&");
        try {
            const { data } = await authApi.getReferredUsers(queryString);
            setReferredUsers(data.referredUsers || []);
            setTotalUsers(data.totalUsers || 0);
        } catch (error) {
            console.error("Failed to fetch data:", error);
            message.error("Không thể tải dữ liệu");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (key: string, value: string) => {
        setFilter((prev) => ({
            ...prev,
            [key]: value,
            page: 1,
        }));
    };

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            const startDate = dates[0].toDate();
            const endDate = dates[1].toDate();
            setFilter((prev) => ({
                ...prev,
                startTime: getUnixTime(startOfDay(startDate)).toString(),
                endTime: getUnixTime(endOfDay(endDate)).toString(),
                page: 1,
            }));
        } else {
            setFilter((prev) => ({
                ...prev,
                startTime: "",
                endTime: "",
                page: 1,
            }));
        }
    };

    // Get date range values for RangePicker
    const getDateRangeValue = (): [Dayjs, Dayjs] | null => {
        if (filter.startTime && filter.endTime) {
            const startTimeNum = parseInt(filter.startTime);
            const endTimeNum = parseInt(filter.endTime);
            if (!isNaN(startTimeNum) && !isNaN(endTimeNum)) {
                const start = dayjs.unix(startTimeNum);
                const end = dayjs.unix(endTimeNum);
                if (start.isValid() && end.isValid()) {
                    return [start, end];
                }
            }
        }
        return null;
    };

    const onChangePageAndSize = (page: number, pageSize: number) => {
        setFilter((prev) => ({ ...prev, page, limit: pageSize }));
    };

    const handleExport = async () => {
        try {
            const queryString = Object.keys(filter)
                .reduce((acc: string[], cur) => {
                    const value = filter[cur as keyof typeof filter];
                    if (value && cur !== "page") {
                        acc.push(`${cur}=${value}`);
                    }
                    return acc;
                }, [])
                .join("&");
            // TODO: Implement export functionality if needed
            message.info("Chức năng xuất file đang được phát triển");
        } catch (error) {
            console.error("Failed to export:", error);
            message.error("Không thể xuất file");
        }
    };

    const dataSource = referredUsers.map((el, index) => ({
        key: index,
        ...el,
    }));

    const columns = [
        {
            title: "User ID",
            dataIndex: "userId",
            key: "userId",
            width: 100,
        },
        {
            title: "Tên",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "ID NGT",
            dataIndex: "refId",
            key: "refId",
            width: 100,
            render: (_: any, { refId }: any) => {
                return refId || "-";
            },
        },
        {
            title: "Email người giới thiệu",
            dataIndex: "refEmail",
            key: "refEmail",
            render: (_: any, { refEmail }: any) => {
                return refEmail || "-";
            },
        },
        {
            title: "Ngày tham gia",
            dataIndex: "createTime",
            key: "createTime",
            render: (_: any, { createTime }: any) => {
                return format(new Date(createTime * 1000), "dd/MM/yyyy HH:mm");
            },
            width: 150,
        },
        {
            title: "Tổng đơn",
            dataIndex: "totalOrders",
            key: "totalOrders",
            width: 120,
            sorter: (a: any, b: any) => a.totalOrders - b.totalOrders,
        },
        {
            title: "Đơn hoàn thành",
            dataIndex: "completedOrders",
            key: "completedOrders",
            width: 160,
            sorter: (a: any, b: any) => a.completedOrders - b.completedOrders,
        },
        {
            title: "Tổng hoa hồng",
            dataIndex: "totalCommission",
            key: "totalCommission",
            sorter: (a: any, b: any) => a.totalCommission - b.totalCommission,
            render: (_: any, { totalCommission }: any) => {
                return parseFloat(totalCommission || 0).toLocaleString("vi-VN") + " đ";
            },
            width: 150,
        },
        {
            title: "Hoa hồng đã duyệt",
            dataIndex: "approvedCommission",
            key: "approvedCommission",
            sorter: (a: any, b: any) => a.approvedCommission - b.approvedCommission,
            render: (_: any, { approvedCommission }: any) => {
                return <span style={{ color: "#52c41a" }}>{parseFloat(approvedCommission || 0).toLocaleString("vi-VN")} đ</span>;
            },
            width: 180,
        },
        {
            title: "Hoa hồng chờ duyệt",
            dataIndex: "pendingCommission",
            key: "pendingCommission",
            sorter: (a: any, b: any) => a.pendingCommission - b.pendingCommission,
            render: (_: any, { pendingCommission }: any) => {
                return <span style={{ color: "#fa8c16" }}>{parseFloat(pendingCommission || 0).toLocaleString("vi-VN")} đ</span>;
            },
            width: 180,
        },
        {
            title: "",
            key: "actions",
            width: 70,
            fixed: "right" as const,
            render: (_: any, r: any) => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => setRefTarget({ userId: r.userId, name: r.name, email: r.email, ref: r.refId })}
                >
                    Sửa
                </Button>
            ),
        },
    ];

    return (
        <div className="p-4">
            <div className="mb-4">
                <h1 className="text-2xl font-bold mb-4"> Danh sách người được giới thiệu</h1>

                {/* Statistics Cards */}
                {statisticsLoading ? (
                    <div className="flex justify-center py-8">
                        <Spin />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <Card>
                            <Statistic title="Tổng người được giới thiệu" value={statistics.totalReferredUsers} valueStyle={{ color: "#1890ff" }} />
                        </Card>
                        <Card>
                            <Statistic
                                title="Tổng hoa hồng"
                                value={statistics.totalCommission}
                                suffix="đ"
                                valueStyle={{ color: "#cf1322" }}
                                formatter={(value) => parseFloat(value as string).toLocaleString("vi-VN")}
                            />
                        </Card>
                        <Card>
                            <Statistic
                                title="Hoa hồng đã duyệt"
                                value={statistics.totalApprovedCommission}
                                suffix="đ"
                                valueStyle={{ color: "#52c41a" }}
                                formatter={(value) => parseFloat(value as string).toLocaleString("vi-VN")}
                            />
                        </Card>
                        <Card>
                            <Statistic
                                title="Hoa hồng chờ duyệt"
                                value={statistics.totalPendingCommission}
                                suffix="đ"
                                valueStyle={{ color: "#fa8c16" }}
                                formatter={(value) => parseFloat(value as string).toLocaleString("vi-VN")}
                            />
                        </Card>
                    </div>
                )}

                {/* Filter and Actions */}
                <div className="flex flex-col md:flex-row gap-4 mb-4 flex-wrap">
                    <RangePicker
                        onChange={handleDateRangeChange}
                        placeholder={["Từ ngày", "Đến ngày"]}
                        allowClear
                        format="DD/MM/YYYY"
                        value={getDateRangeValue()}
                        presets={DATE_PRESETS as any}
                    />
                    <Input
                        placeholder="User ID người giới thiệu"
                        value={filter.refId}
                        onChange={(e) => handleInputChange("refId", e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Input
                        placeholder="Email người giới thiệu"
                        value={filter.refEmail}
                        onChange={(e) => handleInputChange("refEmail", e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <AssignReferrerSearch onPick={setRefTarget} />
                    <Button type="primary" icon={<FontAwesomeIcon icon={faDownload} />} onClick={handleExport}>
                        Xuất Excel
                    </Button>
                </div>
            </div>

            {/* Data Table */}
            <Table
                dataSource={dataSource}
                columns={columns}
                loading={isLoading}
                pagination={{
                    current: filter.page,
                    pageSize: filter.limit,
                    total: totalUsers,
                    showSizeChanger: true,
                    showTotal: (total) => `Tổng ${total} người`,
                    onChange: onChangePageAndSize,
                }}
                scroll={{ x: "max-content" }}
            />

            <ReferrerEditModal
                target={refTarget}
                onClose={() => setRefTarget(null)}
                onSuccess={() => {
                    setRefTarget(null);
                    fetchReferredUsers();
                    fetchStatistics();
                }}
            />
        </div>
    );
}

export default ReferredUsers;

